import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChild } from '../contexts/ChildContext';
import { authApi, notificationsApi, studentApi, teacherApi } from '../services/api';
import NotificationsPopup from './NotificationsPopup';
import { onCreditsChanged } from '../utils/credits';

// For student/teacher the index holds DB library items; for parent it holds
// per-child shortcuts (performans/ödevler/randevular). Both share this union
// so the same render path works.
interface SearchHit {
  kind: 'book' | 'subject' | 'unit' | 'parent-child-action' | 'parent-action';
  id: string | number;
  label: string;
  sublabel?: string;
  bookId?: number;
  coverUrl?: string | null;
  subjectName?: string;
  // Parent-specific
  childId?: string;
  action?: 'performans' | 'odevler' | 'randevular';
}

const TopBar: React.FC = () => {
  const { user } = useAuth();
  const { children: parentChildren, selectChild } = useChild();
  const navigate = useNavigate();
  const base = user ? `/${user.role}` : '';
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  // Fetch the unread notification count so the bell dot reflects reality
  // without requiring the user to open the popup first.
  const loadUnread = useCallback(() => {
    if (!user) return;
    notificationsApi.list()
      .then((res: any) => {
        const rows = res.data ?? [];
        setUnreadCount(rows.filter((r: any) => !(r.is_read ?? false)).length);
      })
      .catch(() => { /* non-critical */ });
  }, [user]);

  // Initial count on mount.
  useEffect(() => { loadUnread(); }, [loadUnread]);

  // B20: poll the unread count every 2 minutes so new notifications surface
  // without opening the popup. Paused while the popup is open (it refreshes
  // itself and reports the count back via onUnreadCountChange) and while the
  // browser tab is hidden; refetch immediately when the tab regains focus.
  useEffect(() => {
    if (!user || notifOpen) return;
    const POLL_MS = 60_000;
    const timer = setInterval(() => { if (!document.hidden) loadUnread(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) loadUnread(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, notifOpen, loadUnread]);

  // Streak: fetch /auth/me so the topbar shows the real current_streak from
  // the DB instead of a hardcoded number. Re-fetched on mount; the backend
  // picks up the latest streak as activity accrues.
  const [streak, setStreak] = useState<number | null>(null);
  useEffect(() => {
    if (user?.role !== 'student') return;
    authApi.me().then((res) => {
      const s = (res.data as any)?.currentStreak;
      if (typeof s === 'number') setStreak(s);
    }).catch(() => { /* leave null */ });
    const loadCredits = () => studentApi.credits()
      .then((res) => { if (typeof res.data?.balance === 'number') setCreditBalance(res.data.balance); })
      .catch(() => {});
    loadCredits();
    // Refresh credit balance every 60 seconds so it stays current after exam registration
    const interval = setInterval(loadCredits, 60_000);
    // Instant update when a booking/cancel changes the balance (library
    // reservation page + Randevu Oluştur modal broadcast the new balance) so
    // the coin doesn't wait up to 60s for the next poll.
    const unsub = onCreditsChanged(setCreditBalance);
    return () => { clearInterval(interval); unsub(); };
  }, [user?.role]);

  // Search: student library + subjects + units hit a client-side index so
  // typing filters in real time without a round-trip per keystroke.
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [index, setIndex] = useState<SearchHit[]>([]);
  const searchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!user) return;

    const coverOf = (book: any, idx: number): string =>
      book?.cover_url ?? book?.cover_image_url ?? `/a-book${((Number(book?.id ?? idx) % 5) || 5)}.png`;

    const buildHits = (rows: any[], isActivation: boolean): SearchHit[] => {
      const hits: SearchHit[] = [];
      const subjectSeen = new Set<string>();
      const unitSeen = new Set<string>();
      rows.forEach((a: any, idx: number) => {
        const book = isActivation ? (a.books ?? a) : a;
        const cover = coverOf(book, idx);
        if (book?.id && book?.title) {
          hits.push({
            kind: 'book',
            id: book.id,
            label: book.title,
            sublabel: book.subject?.name ?? undefined,
            bookId: book.id,
            coverUrl: cover,
          });
        }
        const subj = book?.subject;
        if (subj?.id && subj?.name && !subjectSeen.has(String(subj.id))) {
          subjectSeen.add(String(subj.id));
          hits.push({ kind: 'subject', id: subj.id, label: subj.name, subjectName: subj.name });
        }
        for (const ci of (book?.content_items ?? [])) {
          const unit = ci?.subject_units;
          if (unit?.id && unit?.name && !unitSeen.has(String(unit.id))) {
            unitSeen.add(String(unit.id));
            hits.push({
              kind: 'unit',
              id: unit.id,
              label: unit.name,
              sublabel: book?.title,
              bookId: book?.id,
              coverUrl: cover,
            });
          }
        }
      });
      return hits;
    };

    if (user.role === 'student') {
      studentApi.library().then((res) => {
        setIndex(buildHits(res.data ?? [], true));
      }).catch(() => { /* silent */ });
    } else if (user.role === 'teacher') {
      // Teacher sees branch-filtered books only (books in teacher's subject + school level).
      teacherApi.library().then((res) => {
        setIndex(buildHits(res.data ?? [], false));
      }).catch(() => { /* silent */ });
    } else if (user.role === 'parent') {
      // Parent search indexes per-child shortcuts (performans / ödevler) and
      // global parent pages (randevular). Typing "performans" surfaces one
      // entry per child; clicking pre-selects that child via ChildContext and
      // navigates to the page.
      const hits: SearchHit[] = [];
      for (const c of parentChildren) {
        const fullName = `${c.name} ${c.surname}`.trim();
        hits.push({
          kind: 'parent-child-action',
          id: `perf-${c.id}`,
          label: `Performans — ${fullName}`,
          sublabel: c.className ?? undefined,
          childId: c.id,
          action: 'performans',
        });
        hits.push({
          kind: 'parent-child-action',
          id: `odev-${c.id}`,
          label: `Ödevler — ${fullName}`,
          sublabel: c.className ?? undefined,
          childId: c.id,
          action: 'odevler',
        });
      }
      hits.push({ kind: 'parent-action', id: 'randevular', label: 'Randevular', sublabel: 'Tüm çocuklar', action: 'randevular' });
      setIndex(hits);
    }
  }, [user, parentChildren]);

  const matches = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    // Parent: show all shortcuts on focus (helpful when there are just a
    // handful of per-child actions). Student/teacher keep the type-to-filter
    // behaviour since their libraries can be large.
    if (!q) return user?.role === 'parent' ? index.slice(0, 12) : [];
    return index
      .filter((h) => h.label.toLocaleLowerCase('tr').includes(q) || (h.sublabel?.toLocaleLowerCase('tr').includes(q) ?? false))
      .slice(0, 10);
  }, [index, query, user?.role]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!searchOpen) return;
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [searchOpen]);

  const goToHit = useCallback((h: SearchHit) => {
    setSearchOpen(false);
    setQuery('');
    if (h.kind === 'parent-child-action' && h.childId && h.action) {
      // Pre-select the child in context, then navigate — the destination
      // page reads ChildContext so it loads the right student's data.
      selectChild(h.childId);
      if (h.action === 'performans') navigate('/parent/performans');
      else if (h.action === 'odevler') navigate('/parent/ogrenci-odevleri');
      else if (h.action === 'randevular') navigate('/parent/randevularim');
      return;
    }
    if (h.kind === 'parent-action' && h.action === 'randevular') {
      navigate('/parent/randevularim');
      return;
    }
    if (h.kind === 'book' && h.bookId) {
      // Teachers don't have a kitap-icerik reading view; route them to library instead.
      if (user?.role === 'teacher') navigate('/teacher/kutuphanem', { state: { bookId: h.bookId } });
      else navigate('/student/kitap-icerik', { state: { bookId: h.bookId } });
    } else if (h.kind === 'unit' && h.bookId) {
      if (user?.role === 'teacher') navigate('/teacher/kutuphanem', { state: { bookId: h.bookId } });
      else navigate('/student/kitap-icerik', { state: { bookId: h.bookId } });
    } else if (h.kind === 'subject') {
      navigate(`${base}/kutuphanem`, { state: { subjectFilter: { id: h.id, name: h.label } } });
    }
  }, [base, navigate, selectChild, user?.role]);

  return (
    <div className="flex items-center gap-3 mb-4 relative">
      {/* Search — 804x75, border 2px #C5C5C5, bg #FAFAFA, r=24 */}
      <div ref={searchRef} className="relative flex-1 min-w-0" style={{ maxWidth: 804 }}>
        <div
          className="flex items-center px-4"
          style={{ height: 75, borderRadius: 24, border: '2px solid #C5C5C5', background: '#FAFAFA' }}
        >
          <img src="/a-search.png" alt="" className="w-[20px] h-[20px] object-contain flex-shrink-0 opacity-60" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            placeholder={
              user?.role === 'parent'
                ? 'Öğrenci, performans, ödev veya randevu ara'
                : user?.role === 'teacher'
                  ? 'Ders, kitap ya da test ara'
                  : 'Ders, ünite ya da kitap ara'
            }
            className="flex-1 bg-transparent ml-3 text-[16px] font-medium text-[#2b2b2b] outline-none placeholder:text-gray-400 min-w-0"
            style={{ fontFamily: 'Inter' }}
          />
          {query && (
            <button
              onClick={() => { setQuery(''); setSearchOpen(false); }}
              aria-label="Temizle"
              className="ml-2 text-gray-400 hover:text-gray-600 text-[18px] leading-none w-6 h-6"
            >×</button>
          )}
        </div>
        {searchOpen && (query.trim() || user?.role === 'parent') && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-[16px] shadow-lg border border-gray-200 z-30 overflow-hidden max-h-[400px] overflow-y-auto">
            {matches.length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-[14px]">Eşleşen sonuç bulunamadı.</p>
            ) : (
              matches.map((h) => (
                <button
                  key={`${h.kind}-${h.id}`}
                  onClick={() => goToHit(h)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                >
                  {/* Thumbnail — book covers shown as-is; subject rows get
                      a colored initial badge so the row still has visual
                      weight. */}
                  {h.coverUrl ? (
                    <img
                      src={h.coverUrl}
                      alt=""
                      className="w-[44px] h-[58px] object-cover rounded-[6px] flex-shrink-0 bg-gray-100"
                    />
                  ) : (
                    <div className={`w-[44px] h-[58px] rounded-[6px] flex-shrink-0 flex items-center justify-center text-white text-[18px] font-bold ${
                      h.kind === 'subject' || h.action === 'odevler' ? 'bg-[#e5599b]' : 'bg-[#3973bf]'
                    }`}>
                      {h.label.charAt(0).toLocaleUpperCase('tr')}
                    </div>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide ${
                        h.kind === 'book' ? 'bg-[#3973bf]/10 text-[#3973bf]' :
                        h.kind === 'subject' ? 'bg-[#e5599b]/10 text-[#e5599b]' :
                        h.kind === 'parent-child-action' ? 'bg-[#3973bf]/10 text-[#3973bf]' :
                        h.kind === 'parent-action' ? 'bg-[#e5599b]/10 text-[#e5599b]' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {h.kind === 'book' ? 'Kitap' :
                         h.kind === 'subject' ? 'Ders' :
                         h.kind === 'unit' ? 'Ünite' :
                         h.kind === 'parent-child-action' ? (h.action === 'performans' ? 'Performans' : 'Ödevler') :
                         h.kind === 'parent-action' ? 'Randevu' : ''}
                      </span>
                    </span>
                    <span className="block text-[14px] font-semibold text-[#2b2b2b] truncate">{h.label}</span>
                    {h.sublabel && <span className="block text-[12px] text-gray-500 truncate">{h.sublabel}</span>}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Right icons — slightly left of far right edge, doubled gap */}
      <div className="flex items-center gap-3 xl:gap-6 flex-shrink-0 ml-auto pr-2">
        {/* Streak — student only */}
        {user?.role === 'student' && (
          <div
            className="hidden sm:flex items-center justify-center gap-2 cursor-pointer"
            style={{ width: 121, height: 72, borderRadius: 24, background: '#FAFAFA', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.12)' }}
          >
            <img src="/a-fire.png" alt="" className="w-[20px] h-[26px] object-contain" />
            <span className="text-[18px] font-bold text-[#e5599b]">{streak ?? '—'}</span>
          </div>
        )}

        {/* Kredi bakiyesi — student only. Coin asset from Figma node 855-4761
            (Digipo - Web file). Replaces the placeholder 💳 emoji. */}
        {user?.role === 'student' && creditBalance !== null && (
          <div
            className="hidden md:flex items-center justify-center gap-1.5 cursor-pointer"
            title="Kredi Bakiyesi"
            style={{ width: 100, height: 72, borderRadius: 24, background: '#FAFAFA', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.12)' }}
          >
            <img src="/new-icons/LMS 1.svg" alt="" className="w-[36px] h-[36px] object-contain" />
            <span className="text-[16px] font-bold text-[#3973bf]">{creditBalance}</span>
          </div>
        )}

        {/* Calendar — student only, AND not individual student (kurum/sınıf bağımlı) */}
        {user?.role === 'student' && !(user as any)?.is_individual && (
          <div
            className="hidden md:flex items-center justify-center cursor-pointer hover:brightness-95 transition-all"
            style={{ width: 72, height: 75, borderRadius: 24, background: '#FAFAFA', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.12)' }}
            onClick={() => navigate(`${base}/ogrenci-programi`)}
          >
            <img src="/new-icons/Takvim 1.svg" alt="" className="w-[24px] h-[24px] object-contain" />
          </div>
        )}

        {/* Notifications — 72x75, r=24, bg #FAFAFA, same shadow */}
        <div
          className="flex items-center justify-center relative cursor-pointer hover:brightness-95 transition-all"
          style={{ width: 72, height: 75, borderRadius: 24, background: '#FAFAFA', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.12)' }}
          onClick={() => setNotifOpen(!notifOpen)}
        >
          <img src="/new-icons/Notif 2.svg" alt="" className="w-[24px] h-[24px] object-contain" />
          {unreadCount > 0 && <div className="absolute top-4 right-4 w-2.5 h-2.5 bg-[#e5599b] rounded-full" />}
        </div>

        {/* Profile — 201x75, r=24, bg #FAFAFA, same shadow */}
        <div
          className="flex items-center justify-center gap-2.5 cursor-pointer hover:brightness-95 transition-all"
          style={{ width: 201, height: 75, borderRadius: 24, background: '#FAFAFA', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.12)' }}
          onClick={() => navigate(`${base}/profil`)}
        >
          {/* AP35: yüklü profil fotoğrafı varsa ikonun yerine onu göster */}
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="w-[26px] h-[26px] rounded-full object-cover" />
          ) : (
            <img src="/new-icons/Profile 1.svg" alt="" className="w-[24px] h-[24px] object-contain" />
          )}
          <span className="text-[17px] font-semibold text-[#3973bf]">Profil</span>
        </div>
      </div>

      {/* Notifications Popup */}
      <NotificationsPopup
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        onUnreadCountChange={setUnreadCount}
      />
    </div>
  );
};

export default TopBar;
